import { Injectable, isDevMode, type OnDestroy } from '@angular/core';
import { Observable, type Subscriber, type Subscription } from 'rxjs';
import { AccountAdvisorService } from '../service';
import { AdvisorDetailsModel } from '../model';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private readonly subscription: Subscription;

  private readonly subscribers = new Map<string, Set<Subscriber<boolean>>>();
  private enabledFeatureFlags: Set<string> | null = null;

  constructor(accountAdvisorService: AccountAdvisorService) {
    this.subscription = accountAdvisorService.advisorDetails.observable.subscribe({
      next: (data: AdvisorDetailsModel): void => {
        const newFeatureFlags = new Set(data.enabledFeatureFlags);

        if (this.enabledFeatureFlags) {
          newFeatureFlags.forEach((flag) => {
            if (!this.enabledFeatureFlags!.has(flag)) {
              this.subscribers.get(flag)?.forEach((subscriber) => subscriber.next(true));
            }
          });

          this.enabledFeatureFlags.forEach((flag) => {
            if (!newFeatureFlags.has(flag)) {
              this.subscribers.get(flag)?.forEach((subscriber) => subscriber.next(false));
            }
          });
        } else {
          this.subscribers.forEach((subscribers, flag) =>
            subscribers.forEach((subscriber) => subscriber.next(newFeatureFlags.has(flag))),
          );
        }

        this.enabledFeatureFlags = newFeatureFlags;
      },
    });

    if (isDevMode()) {
      Object.defineProperties(window, {
        enableFeatureFlag: {
          value: (flag: string): void => {
            if (!this.enabledFeatureFlags) {
              console.error('Feature flags not initialized yet.');
              return;
            }

            if (!this.enabledFeatureFlags.has(flag)) {
              console.log('Enabling feature flag:', flag);
              this.enabledFeatureFlags.add(flag);

              this.subscribers.get(flag)?.forEach((subscriber) => subscriber.next(true));
            } else {
              console.log('Feature flag already enabled:', flag);
            }
          },
          enumerable: false,
          writable: false,
          configurable: true,
        },
        disableFeatureFlag: {
          value: (flag: string): void => {
            if (!this.enabledFeatureFlags) {
              console.error('Feature flags not initialized yet.');
              return;
            }

            if (this.enabledFeatureFlags.has(flag)) {
              console.log('Disabling feature flag:', flag);
              this.enabledFeatureFlags.delete(flag);

              this.subscribers.get(flag)?.forEach((subscriber) => subscriber.next(false));
            } else {
              console.log('Feature flag already disabled:', flag);
            }
          },
          enumerable: false,
          writable: false,
          configurable: true,
        },
      });
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public isFeatureEnabled(featureName: string): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      if (!this.subscribers.has(featureName)) {
        this.subscribers.set(featureName, new Set());
      }

      this.subscribers.get(featureName)!.add(subscriber);

      if (this.enabledFeatureFlags) {
        subscriber.next(this.enabledFeatureFlags.has(featureName));
      }

      return () => {
        this.subscribers.get(featureName)!.delete(subscriber);
      };
    });
  }
}
